import { Component, inject, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { catchError, concatMap, first, map, tap } from 'rxjs/operators';
import { BasePayrollComponent } from '../../../../components/base/base-payroll.component';
import {
  CreatePayrollArgs,
  EmployeePay,
  MasterPayrollObject,
  Payline,
  PaylineType,
  PayrollConfig,
  PayrollStartData,
  PayrollStatuses,
  PayTypes,
  UsedPayroll
} from '../../../../models/payroll';
import { PayrollService } from '../../../../services/payroll.service';
import { PayrollManagement } from '../../../../tools/payroll-management';
import { IPayrollCorrectionsPage } from '../../../../guards/can-deactivate-correction-payroll.guard';
import { AuditService } from '../../../../services/audit.service';
import { ModalService } from '../../../../services/modal.service';
import { Store } from '@ngxs/store';
import { BillingSelectModalComponent } from '../../billing/billing-select-modal.component';
import { PayrollInfo } from '../../../../models/payroll-info';
import { ToastService } from '../../../../services/toast.service';

@Component({
  selector: 'lib-payroll-corrections',
  templateUrl: './payroll-corrections.component.html',
  styleUrls: ['./payroll-corrections.component.css'],
  standalone: false
})
export class PayrollCorrectionsComponent
  extends BasePayrollComponent
  implements OnInit, IPayrollCorrectionsPage
{
  @ViewChild('pendingPaysModalRef', { static: true }) public pendingPaysModalRef: TemplateRef<any>;

  get paylineTypes() {
    return PaylineType;
  }

  constructor(
    private auditService: AuditService,
    public modalService: ModalService,
    private ngbModalService: NgbModal,
    public payrollMgmt: PayrollManagement,
    private payrollService: PayrollService,
    private router: Router,
    private store: Store
  ) {
    super();
  }

  public isLoading: boolean;
  public paylines: UniquePayline[];
  public payroll: MasterPayrollObject; // active correction payroll in progress
  public payrollOptions: UsedPayroll[] = []; // excludes payrolls where no pays are eligible to void
  public payrollOptionsHidden: UsedPayroll[] = []; // paydates not yet shown in the list, historical
  public payrolls: { [year: number]: { [seq: number]: EmployeePay[] } } = {};
  public render: boolean;
  public selPayline: UniquePayline = null;
  public selPayroll: UsedPayroll = null;
  public selSource: string = null;
  public showFormErrors = false;
  public startData: PayrollStartData;
  public Math: any = Math;

  private destroy$ = new Subject<void>();
  private payrollInfo: PayrollInfo;
  private readonly toastService = inject(ToastService);

  ngOnInit(): void {
    this.payrollService
      .getPayrollStartData(this.clientCode)
      .pipe(
        concatMap(startData => {
          this.startData = startData;
          const payrolls = this.startData.OpenOrHeldQuarterPayrolls || [];

          this.payrollOptions = [];
          this.payrollOptionsHidden = [...payrolls];

          const activePayroll = this.startData.ActivePayroll;
          if (activePayroll != null && activePayroll.PayType == PayTypes.Adjustment) {
            return this.payrollService.getPayrollInfo(
              this.clientCode,
              activePayroll.Year,
              activePayroll.SequenceNumber
            );
          } else {
            return of(null);
          }
        }),
        map((info: PayrollInfo | null) => {
          if (info) {
            this.payrollInfo = info;
          }

          if (this.startData.ActivePayroll != null) {
            const p = this.startData.ActivePayroll;
            if (p.PayType == PayTypes.Adjustment) {
              this._getActiveCorrectionPayroll(p);
            } else {
              // there is another type of payroll in progress. user shouldn't be here
              this.router.navigate([`/client/${this.clientCode}/payroll-landing`]);
            }
          } else {
            // fetch employee pays history for most recent 3 completed payrolls
            this.loadNextPayrolls(3);
          }
        })
      )
      .subscribe(() => {
        this.render = true;
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
  }

  public cancelPayroll() {
    const msg = 'Are you sure you want to cancel this payroll?';
    this.modalService.showConfirmModal(msg, 'Do not Cancel Payroll', 'Cancel Payroll');
    this.modalService.confirmModalCallback.pipe(first()).subscribe((confirm: boolean) => {
      if (confirm) {
        this.payrollMgmt
          .cancelPayroll(this.clientCode, this.payroll.Year, this.payroll.SequenceNumber)
          .subscribe(() => {
            // set IsCancelled so deactivate guard logic knows
            // not to update the payroll's status
            this.payroll.IsCancelled = true;
            this.router.navigate([`/client/${this.clientCode}/payroll-landing`]);
          });
      }
    });
  }

  // load three payrolls at a time in order to paginate the history data fetched
  // to reduce performance impacts of this endpoint for high frequency clients with many employees
  // if one of the payrolls fetched is ineligible to void, move to fetch the next paydate in the list
  public loadNextPayrolls(count: number) {
    this.isLoading = true;
    if (!this.payrollOptionsHidden.length) {
      return;
    }

    // get next hidden payrolls
    const payrolls = this.payrollOptionsHidden.splice(0, count);

    const obs: Observable<boolean>[] = [];

    for (let p of payrolls) {
      // load observables to fetch the history
      obs.push(
        this._getEmployeePaysHistory(p.Year, p.PayrollSeq).pipe(
          map(history => {
            if (!history) {
              return false;
            } else {
              // check if payroll is eligible to void
              // need at least one payline where payline type is not 'void'
              const eligible = this._isPayrollEligibleToVoid(p);

              if (eligible) {
                // not eligible to be voided, remove from payroll options
                this.payrollOptions.push(p);
              }

              return eligible;
            }
          })
        )
      );
    }

    // fetch history, report back with count of payrolls
    // that were ineligible to void
    forkJoin(obs).subscribe(response => {
      const ineligible = response.filter(x => !x).length;

      if (ineligible) {
        this.loadNextPayrolls(ineligible);
      } else {
        this.isLoading = false;
        this._sortPayrollOptions();
      }
    });
  }

  public onPayrollChange() {
    if (!this.selPayroll) {
      this.selPayline = null;
    } else {
      const employeePays = this.payrolls[this.selPayroll.Year][this.selPayroll.PayrollSeq];
      const pays = this.payrollMgmt.sortEmployeesByLastName(employeePays);
      this.paylines = pays.flatMap(e =>
        e.Paylines.filter(x => x.PaylineType != PaylineType.Void).map(p => new UniquePayline(e, p))
      );
    }
  }

  public reviewAndSubmit() {
    if (!this.payroll) {
      return;
    }

    this.router.navigate([
      `/client/${this.clientCode}/payroll-corrections-preview/${this.payroll.Year}/${this.payroll.SequenceNumber}`
    ]);
  }

  public showBillings() {
    const modalRef = this.ngbModalService.open(BillingSelectModalComponent, {
      size: 'lg',
      backdrop: 'static',
      keyboard: false,
      windowClass: 'centered'
    });

    const c = modalRef.componentInstance;

    c.auditKey = 'correction-billing-override';
    c.payroll = this.payroll;

    c.onDismiss = () => {
      modalRef.dismiss();
    };
    c.onClose = () => {
      modalRef.close();
    };

    modalRef.result.then(
      // confirm
      () => {
        this.reviewAndSubmit();
      },
      // cancel
      () => {}
    );
  }

  public startPayrollCorrection() {
    // button should already be disabled under any of these conditions
    if (!this.selPayroll || !this.selPayline || !this.selSource) {
      return;
    }

    if (this.payroll) {
      this._resumePayroll();
      return;
    }

    const personId = this.selPayline.EmployeePay.PersonId;

    const pdi = this.startData.Locations[0].NextPayDates[0];
    pdi.TargetPayDate = this.selPayroll.PayDate;

    const args = new CreatePayrollArgs(
      this.clientCode,
      pdi,
      this.selPayroll.Calendars,
      PayTypes.Adjustment,
      new PayrollConfig(),
      null,
      null,
      null,
      this.selSource
    );
    args.PayrollToCorrect = this.selPayroll.PayrollSeq;

    this.payrollMgmt
      .createPayroll(args)
      .pipe(
        tap(payroll => {
          this.auditService.auditPayroll(
            'start-payroll-clicked',
            {
              Calendars: payroll.Calendars,
              ClientCode: payroll.ClientCode,
              LocationId: payroll.LocationId,
              PayDate: payroll.PayDate,
              PayrollType: payroll.PayType,
              SequenceNumber: payroll.SequenceNumber
            },
            {
              PayrollLabel: 'Correction'
            }
          );
          return of(payroll);
        }),
        concatMap(payroll => {
          const empPay = payroll.EmployeePays.find(
            x => x.PersonId == this.selPayline.EmployeePay.PersonId
          );

          // find the payline that was selected based on NetAmountPaid
          const pay = empPay.Paylines.find(
            x =>
              Math.abs(x.NetAmountPaid) == Math.abs(this.selPayline.Payline.NetAmountPaid) &&
              !x.OkToPay
          );

          // set OkToPay true to let API know this payline should be voided
          pay.OkToPay = true;
          this.selPayline.Payline = pay;
          this.selPayline.EmployeePay = empPay;

          return of(payroll);
        }),
        concatMap(payroll => {
          const paylines = payroll.EmployeePays.find(
            x => x.PersonId == this.selPayline.EmployeePay.PersonId
          ).Paylines;

          return this.payrollService
            .calculatePaylines(
              this.clientCode,
              personId,
              payroll.Year,
              payroll.SequenceNumber,
              paylines,
              false,
              true
            )
            .pipe(
              map(paylines => {
                // NOTE: only updating the cache with the payline that was calculated
                // in order to preserve NetAmountPaid value
                for (let p of paylines) {
                  if (p.PaylineId != this.selPayline.Payline.PaylineId) {
                    continue;
                  }

                  const empPaylines = this.selPayline.EmployeePay.Paylines;

                  let payline = empPaylines.find(
                    x => x.PaylineId == this.selPayline.Payline.PaylineId
                  );
                  const index = empPaylines.indexOf(payline);
                  empPaylines[index] = p;
                }

                this.payrollMgmt.updateEmployeePay(
                  payroll.ClientCode,
                  payroll.Year,
                  payroll.SequenceNumber,
                  this.selPayline.EmployeePay
                );
                return payroll;
              })
            );
        })
      )
      .subscribe(payroll => {
        this.router.navigate([
          `/client/${this.clientCode}/payroll-corrections/${payroll.Year}/${payroll.SequenceNumber}/person/${personId}/${this.selPayline.Payline.PaylineId}`
        ]);
      });
  }

  private _calcPaylineAndNavigate() {
    this.render = false;

    // replace the payline data with what we had from the correction payroll
    const empPay = this.payroll.EmployeePays.find(
      x => x.PersonId == this.selPayline.EmployeePay.PersonId
    );

    // find the payline that was selected based on NetAmountPaid
    const pay = empPay.Paylines.find(
      x =>
        Math.abs(x.NetAmountPaid) == Math.abs(this.selPayline.Payline.NetAmountPaid) && !x.OkToPay
    );

    // set OkToPay true to let API know this payline should be voided
    pay.OkToPay = true;
    this.selPayline.Payline = pay;
    this.selPayline.EmployeePay = empPay;

    return this.payrollService
      .calculatePaylines(
        this.clientCode,
        this.selPayline.EmployeePay.PersonId,
        this.payroll.Year,
        this.payroll.SequenceNumber,
        empPay.Paylines,
        false,
        true
      )
      .subscribe(paylines => {
        // NOTE: only updating the cache with the payline that was calculated
        // in order to preserve NetAmountPaid value
        for (let p of paylines) {
          if (p.PaylineId != this.selPayline.Payline.PaylineId) {
            continue;
          }

          const empPaylines = this.selPayline.EmployeePay.Paylines;

          let payline = empPaylines.find(x => x.PaylineId == this.selPayline.Payline.PaylineId);
          const index = empPaylines.indexOf(payline);
          empPaylines[index] = p;
        }

        this.payrollMgmt.updateEmployeePay(
          this.payroll.ClientCode,
          this.payroll.Year,
          this.payroll.SequenceNumber,
          this.selPayline.EmployeePay
        );

        this.router.navigate([
          `/client/${this.clientCode}/payroll-corrections/${+this.selPayroll.Year}/${+this.payroll
            .SequenceNumber}/person/${this.selPayline.EmployeePay.PersonId}/${
            this.selPayline.Payline.PaylineId
          }`
        ]);
      });
  }

  private _getActiveCorrectionPayroll(p: MasterPayrollObject) {
    this.payrollMgmt.getPayroll(p.ClientCode, p.Year, p.SequenceNumber).subscribe(payroll => {
      this.payroll = payroll;
      this.payrollOptionsHidden = [];

      this.selSource = this.payrollInfo.CorrectionSource;
      this.selPayroll = this.startData.OpenOrHeldQuarterPayrolls.find(
        x => +x.Year == p.Year && x.PayrollSeq == this.payrollInfo.CorrectionSequence
      );

      const isValid = this._validateData();

      if (!isValid) {
        window.location.reload();
        return;
      }

      this.payroll.EmployeePays = this.payrollMgmt.sortEmployeesByLastName([
        ...this.payroll.EmployeePays
      ]);

      this._getAssociatedPayrollHistory();
    });
  }

  private _getAssociatedPayrollHistory() {
    // get the employee pay history from the original payroll being voided
    this._getEmployeePaysHistory(this.selPayroll.Year, this.selPayroll.PayrollSeq).subscribe(() => {
      const employeePays = this.payrolls[this.selPayroll.Year][this.selPayroll.PayrollSeq];
      this.payrollOptions = [this.selPayroll];
      const pays = this.payrollMgmt.sortEmployeesByLastName(employeePays);

      const paidPaylines = {};

      this.payroll.EmployeePays.flatMap(e =>
        e.Paylines.filter(x => x.OkToPay).forEach(x => {
          if (!paidPaylines[e.EmployeeNumber]) {
            paidPaylines[e.EmployeeNumber] = [];
          }

          paidPaylines[e.EmployeeNumber].push(x.PaylineId);
        })
      );

      this.paylines = pays.flatMap(employee => {
        return employee.Paylines.filter(x => x.PaylineType != PaylineType.Void).map(
          payline =>
            new UniquePayline(
              employee,
              payline,
              // if the payline is in the current active payroll
              // and set OkToPay, it is pending to be voided
              paidPaylines[employee.EmployeeNumber] &&
                paidPaylines[employee.EmployeeNumber].includes(payline.PaylineId)
            )
        );
      });
    });
  }

  // us 29930 - remove previous front end caching work as large clients can
  // exceed session storage quotas
  // created in task #25571
  private _getEmployeePaysHistory(year: string, seq: number): Observable<EmployeePay[]> {
    return this.payrollService.getEmployeePaysHistory(this.clientCode, year, seq, false).pipe(
      catchError(() => {
        return of(null);
      }),
      tap(pays => {
        if (!this.payrolls[year]) {
          this.payrolls[year] = {};
        }

        this.payrolls[year][seq] = pays;
      })
    );
  }
  // task #25574: exclude any payrolls that have no eligible pays to void/
  // at least one payline where PaylineType != PaylineType.Void
  private _isPayrollEligibleToVoid(p: UsedPayroll) {
    let eligible = true;

    if (!this.payrolls[p.Year]) {
      return false;
    }

    if (this.payrolls[p.Year][p.PayrollSeq]) {
      const canVoid = this.payrolls[p.Year][p.PayrollSeq].some(e =>
        e.Paylines.some(p => p.PaylineType != PaylineType.Void && p.IsVoided != 'Y')
      );

      if (!canVoid) {
        eligible = false;
      }
    } else if (Object.keys(this.payrolls[p.Year]).includes(p.PayrollSeq.toString())) {
      // handle situation where GetEmployeePaysHistory came back with an error
      // key exists but value is undefined
      eligible = false;
    }

    return eligible;
  }

  private _resumePayroll() {
    if (this.payroll.Status == PayrollStatuses.Hold) {
      this.payrollService
        .updatePayrollStatus(
          this.payroll.ClientCode,
          this.payroll.Year,
          this.payroll.SequenceNumber,
          PayrollStatuses.Started
        )
        .subscribe(() => {
          // update the payroll in cache with the correct status
          this.payrollMgmt.updateCachedStatus(
            this.payroll.ClientCode,
            this.payroll.Year,
            this.payroll.SequenceNumber,
            PayrollStatuses.Started
          );
          this._calcPaylineAndNavigate();
        });
    } else {
      this._calcPaylineAndNavigate();
    }
  }

  private _sortPayrollOptions() {
    // observables can complete out of order
    // sort payrollOptions descending by payroll year and sequence
    this.payrollOptions = this.payrollOptions.sort((a, b) => {
      if (a.Year == b.Year) {
        return a.PayrollSeq > b.PayrollSeq ? -1 : 1;
      }

      return a.Year > b.Year ? -1 : 1;
    });
  }

  private _validateData() {
    // if there are no paylines OkToPay, the user may have backed out of the previous screen
    // after adding only 1 payline. cancel the payroll so the screen state refreshes.
    const paidPaylines = this.payroll.EmployeePays.flatMap(e => e.Paylines.filter(x => x.OkToPay));

    if (!paidPaylines.length) {
      this.payrollMgmt
        .cancelPayroll(this.payroll.ClientCode, this.payroll.Year, this.payroll.SequenceNumber)
        .subscribe(() => {
          this.payroll = null;
        });

      return false;
    }

    // ERROR: bad data condition. this should only happen in dev/QA, so raise a flag otherwise
    // the wrong payroll info record was pulled or the CorrectionSequence value was not set properly.
    // cancel the payroll and clear the UI state so the user can start a new correction.
    // NOTE: multiple PRI records can be left over if the payroll is canceled in the base or
    // through swagger. canceling here should properly clear the PRI's sequence number
    if (!this.selPayroll) {
      this.payrollService
        .cancelPayroll(this.clientCode, this.payroll.Year, this.payroll.SequenceNumber)
        .subscribe(() => {
          this.payroll = null;
          throw new Error('Error fetching CorrectionSequence from payroll info record');
        });

      return false;
    }

    return true;
  }
}

export class UniquePayline {
  EmployeePay: EmployeePay;
  Payline: Payline;
  IsPending: boolean;

  constructor(employeePay: EmployeePay, payline: Payline, isPending: boolean = false) {
    this.EmployeePay = employeePay;
    this.Payline = payline;
    this.IsPending = isPending;
  }
}
